/**
 * Mod-only "Search the web (Vision)" reverse-image modal.
 *
 * Triggered from the photo lightbox menu. Fetches
 * GET /v1/mod/photos/{id}/web-detection via ApiClient and shows the distilled
 * Google Vision Web Detection evidence: a strength tier, the best-guess label,
 * the distinct real-human identities the photo is published under, the pages
 * carrying an exact/partial match, and matching-image thumbnails.
 *
 * Mod-only surface, so its body copy is hardcoded English (mirrors
 * photo-reuse-lightbox.js and the moderation i18n carve-out). All
 * server-supplied text (identity names, page titles) is written through
 * `textContent`, never `innerHTML` (the web is adversarial input).
 */
(function () {
    'use strict';

    var modalEl = null;
    var state = {
        photoId: '',
        requestSeq: 0,
        priorBodyOverflow: null,
    };

    function ensureModal() {
        if (modalEl) return modalEl;
        modalEl = document.createElement('div');
        modalEl.id = 'photo-web-detection-lightbox-modal';
        modalEl.className = 'hidden fixed inset-0 z-[10010]';
        modalEl.innerHTML = ''
            + '<div class="pwd-backdrop fixed inset-0 bg-black/50 flex items-end justify-center sm:items-center sm:p-6">'
            + '  <div class="pwd-card bg-white dark:bg-gray-900 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[90dvh] overflow-hidden shadow-2xl">'
            + '    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">'
            + '      <div>'
            + '        <h2 class="m-0 text-sm font-semibold text-gray-900 dark:text-gray-100">Search the web (Vision)</h2>'
            + '        <p class="m-0 mt-0.5 text-xs text-gray-500 dark:text-gray-400">Where this image appears on the public web, and under what identities.</p>'
            + '      </div>'
            + '      <button type="button" class="pwd-close p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 text-lg leading-none border-0 bg-transparent cursor-pointer" aria-label="Close">&times;</button>'
            + '    </div>'
            + '    <div class="pwd-body flex-1 overflow-y-auto p-3"></div>'
            + '  </div>'
            + '</div>';

        document.body.appendChild(modalEl);

        var backdrop = modalEl.querySelector('.pwd-backdrop');
        var card = modalEl.querySelector('.pwd-card');
        var closeBtn = modalEl.querySelector('.pwd-close');

        backdrop.addEventListener('click', function (e) {
            if (e.target === backdrop) close();
        });
        card.addEventListener('click', function (e) { e.stopPropagation(); });
        closeBtn.addEventListener('click', close);
        document.addEventListener('keydown', function (e) {
            if (!modalEl.classList.contains('hidden') && e.key === 'Escape') close();
        });

        return modalEl;
    }

    function bodyHost() {
        return modalEl.querySelector('.pwd-body');
    }

    function clearHost() {
        var host = bodyHost();
        while (host.firstChild) host.removeChild(host.firstChild);
        return host;
    }

    function renderMessage(text) {
        var host = clearHost();
        var p = document.createElement('p');
        p.className = 'm-0 px-2 py-8 text-center text-sm text-gray-500 dark:text-gray-400';
        p.textContent = text;
        host.appendChild(p);
    }

    function renderLoading() {
        var host = clearHost();
        var p = document.createElement('p');
        p.className = 'm-0 px-2 py-8 text-center text-sm text-gray-500 dark:text-gray-400';
        p.textContent = 'Searching the web…';
        host.appendChild(p);
    }

    function strengthBadge(strength) {
        var span = document.createElement('span');
        var base = 'inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ';
        var tone;
        var label;
        if (strength === 'strong') {
            tone = 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
            label = 'strong';
        } else if (strength === 'weak') {
            tone = 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
            label = 'weak';
        } else {
            tone = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
            label = 'none';
        }
        span.className = base + tone;
        span.textContent = label;
        return span;
    }

    function domainOf(url) {
        try {
            return new URL(url).hostname.replace(/^www\./, '');
        } catch (e) {
            return url;
        }
    }

    function identityChips(names) {
        var wrap = document.createElement('div');
        wrap.className = 'flex flex-wrap gap-1.5 mt-1';
        names.forEach(function (name) {
            var chip = document.createElement('span');
            chip.className = 'inline-flex items-center px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300 text-[11px] font-medium';
            chip.textContent = name;
            wrap.appendChild(chip);
        });
        return wrap;
    }

    function pageRow(page) {
        var row = document.createElement('a');
        row.className = 'flex flex-col px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/60 no-underline';
        // The endpoint returns absolute external URLs. Only accept http(s).
        if (/^https?:\/\//i.test(page.url)) {
            row.href = page.url;
            row.target = '_blank';
            row.rel = 'noopener noreferrer';
        }
        var title = document.createElement('span');
        title.className = 'text-sm text-sky-600 dark:text-sky-400 truncate';
        title.textContent = page.title || domainOf(page.url);
        row.appendChild(title);
        var dom = document.createElement('span');
        dom.className = 'text-[11px] text-gray-400 dark:text-gray-500 truncate';
        dom.textContent = domainOf(page.url);
        row.appendChild(dom);
        return row;
    }

    // Static, trusted icon markup — no interpolation, so assigning it via
    // innerHTML carries none of the adversarial-input risk that the file's
    // server-supplied text (identity names, URLs) does.
    var ICON_EXTERNAL_LINK = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h6"/></svg>';
    var ICON_IMAGE_OFF = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="22" y1="2" y2="22"/><path d="M10.41 10.41a2 2 0 1 1-2.83-2.83"/><line x1="13.5" x2="6" y1="13.5" y2="21"/><line x1="18" x2="21" y1="12" y2="15"/><path d="M3.59 3.59A1.99 1.99 0 0 0 3 5v14a2 2 0 0 0 2 2h14c.55 0 1.05-.22 1.41-.59"/><path d="M21 15V5a2 2 0 0 0-2-2H9"/></svg>';

    function iconSpan(markup, className) {
        var span = document.createElement('span');
        span.className = className;
        span.innerHTML = markup;
        return span;
    }

    // Fills a tile when its remote image fails to load (404, hotlink-blocked),
    // replacing the browser's broken-image glyph with a muted "gone" marker.
    function brokenPlaceholder() {
        var box = document.createElement('span');
        box.className = 'absolute inset-0 flex flex-col items-center justify-center gap-0.5 text-gray-600 dark:text-gray-400';
        box.appendChild(iconSpan(ICON_IMAGE_OFF, 'block'));
        var label = document.createElement('span');
        label.className = 'text-[9px] leading-none uppercase tracking-wide';
        label.textContent = 'gone';
        box.appendChild(label);
        return box;
    }

    function thumbnailTile(url) {
        var domain = domainOf(url);
        var tile = document.createElement('a');
        tile.className = 'group relative block w-14 h-14 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0 no-underline ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-2 hover:ring-sky-400 dark:hover:ring-sky-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:focus-visible:ring-sky-500 transition-shadow';
        // Wire up navigation and its labelling only for http(s), mirroring
        // pageRow's guard. The caller already filters; this keeps the tile
        // self-safe if reused (no "opens in a new tab" claim without an href).
        if (/^https?:\/\//i.test(url)) {
            tile.href = url;
            tile.target = '_blank';
            tile.rel = 'noopener noreferrer';
            tile.title = 'Open image (' + domain + ')';
            tile.setAttribute('aria-label', 'Open matching image on ' + domain + ' in a new tab');
        }

        var img = document.createElement('img');
        img.src = url;
        // Decorative: the anchor's aria-label carries the accessible name.
        img.alt = '';
        img.loading = 'lazy';
        img.referrerPolicy = 'no-referrer';
        img.className = 'w-full h-full object-cover';
        var showedPlaceholder = false;
        img.addEventListener('error', function () {
            if (showedPlaceholder) return;
            showedPlaceholder = true;
            if (img.parentNode === tile) tile.removeChild(img);
            tile.appendChild(brokenPlaceholder());
            // Keep the accessible name in sync with the visible "gone" state.
            tile.title = 'Image unavailable (' + domain + ')';
            tile.setAttribute('aria-label', 'Matching image on ' + domain + ' (image unavailable)');
        });
        tile.appendChild(img);

        // External-link glyph, revealed on hover or keyboard focus, so the tile
        // reads as clickable. z-10 keeps it above the broken-image placeholder;
        // top-right keeps it clear of the centered "gone" label.
        tile.appendChild(iconSpan(
            ICON_EXTERNAL_LINK,
            'pointer-events-none absolute z-10 top-0.5 right-0.5 flex items-center justify-center w-4 h-4 rounded bg-black/55 text-white opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity'
        ));

        return tile;
    }

    function thumbnailStrip(urls) {
        var wrap = document.createElement('div');
        wrap.className = 'flex flex-wrap gap-2 mt-2';
        urls.forEach(function (url) {
            if (!/^https?:\/\//i.test(url)) return;
            wrap.appendChild(thumbnailTile(url));
        });
        return wrap;
    }

    function sectionLabel(text) {
        var el = document.createElement('p');
        el.className = 'm-0 mt-3 mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500';
        el.textContent = text;
        return el;
    }

    function renderResult(result) {
        var host = clearHost();

        // Header: strength + best guess.
        var header = document.createElement('div');
        header.className = 'flex items-center gap-2 px-2 py-1';
        header.appendChild(strengthBadge(result.strength));
        if (result.best_guess_label) {
            var guess = document.createElement('span');
            guess.className = 'text-sm text-gray-700 dark:text-gray-200 truncate';
            guess.textContent = 'Best guess: ' + result.best_guess_label;
            header.appendChild(guess);
        }
        host.appendChild(header);

        // Match counts.
        var counts = document.createElement('p');
        counts.className = 'm-0 px-2 text-xs text-gray-500 dark:text-gray-400';
        var full = result.full_match_pages || 0;
        var partial = result.partial_match_pages || 0;
        if (full || partial) {
            counts.textContent = full + (full === 1 ? ' page' : ' pages') + ' with an exact match'
                + (partial ? ', ' + partial + ' partial.' : '.');
        } else {
            counts.textContent = 'No exact or partial web matches. Only "visually similar" noise, or nothing.';
        }
        host.appendChild(counts);

        // Recognized public figure caveat. Identities listed below already
        // exclude the figure's own name, so any that remain are other accounts
        // using the photo as their face.
        if (result.is_recognized_public_entity) {
            var caveat = document.createElement('p');
            caveat.className = 'm-0 px-2 mt-1 text-[11px] text-amber-600 dark:text-amber-400';
            caveat.textContent = 'Matches a recognized public figure. Weak signal on its own, fans legitimately reuse such photos. Any identities listed below are other names this photo circulates under.';
            host.appendChild(caveat);
        }

        // Distinct identities.
        var names = result.distinct_identity_names || [];
        if (names.length) {
            host.appendChild(sectionLabel('Published under ' + names.length
                + (names.length === 1 ? ' identity' : ' distinct identities')));
            var chipsWrap = document.createElement('div');
            chipsWrap.className = 'px-2';
            chipsWrap.appendChild(identityChips(names));
            host.appendChild(chipsWrap);
            if (result.name_conflicts_profile) {
                var conflict = document.createElement('p');
                conflict.className = 'm-0 px-2 mt-1 text-[11px] text-rose-600 dark:text-rose-400';
                conflict.textContent = 'At least one conflicts with this profile\'s stated name.';
                host.appendChild(conflict);
            }
        }

        // Matching pages.
        var pages = result.sample_pages || [];
        if (pages.length) {
            host.appendChild(sectionLabel('Where it appears'));
            var list = document.createElement('div');
            list.className = 'flex flex-col';
            pages.forEach(function (p) { list.appendChild(pageRow(p)); });
            host.appendChild(list);
        }

        // Thumbnails.
        var thumbs = result.thumbnails || [];
        if (thumbs.length) {
            host.appendChild(sectionLabel('Matching images'));
            var thumbWrap = document.createElement('div');
            thumbWrap.className = 'px-2';
            thumbWrap.appendChild(thumbnailStrip(thumbs));
            host.appendChild(thumbWrap);
        }
    }

    /**
     * The API error envelope carries `error` as an object
     * ({type, code, message} from ApiException::getApiData), older endpoints
     * as a bare string. Extract a displayable string either way. Writing the
     * raw object to textContent would render "[object Object]".
     */
    function errorText(body) {
        var err = body && body.error;
        if (typeof err === 'string' && err) {
            return err;
        }
        if (err && typeof err.message === 'string' && err.message) {
            return err.message;
        }
        return 'Search failed.';
    }

    function fetchAndRender() {
        renderLoading();

        if (!window.ApiClient) {
            renderMessage('ApiClient unavailable.');
            return;
        }

        var seq = ++state.requestSeq;
        var photoId = state.photoId;

        window.ApiClient.request(
            '/mod/photos/' + encodeURIComponent(photoId) + '/web-detection',
            { method: 'GET' }
        ).then(function (res) {
            if (res && typeof res.json === 'function') {
                return res.json().then(function (body) {
                    return { ok: res.ok, status: res.status, body: body };
                });
            }
            return { ok: true, status: 200, body: res };
        }).then(function (envelope) {
            if (seq !== state.requestSeq || modalEl.classList.contains('hidden')) {
                return;
            }
            if (envelope.ok && envelope.body && envelope.body.status === 'ok' && envelope.body.result) {
                renderResult(envelope.body.result);
                return;
            }
            if (envelope.ok && envelope.body && envelope.body.status === 'unavailable') {
                renderMessage('Reverse web search is unavailable right now (not configured, disabled, or the fetch failed).');
                return;
            }
            renderMessage(errorText(envelope.body));
        }).catch(function (err) {
            if (seq !== state.requestSeq || modalEl.classList.contains('hidden')) {
                return;
            }
            renderMessage((err && err.message) ? err.message : 'Network error.');
        });
    }

    function open(photoId) {
        ensureModal();
        state.photoId = String(photoId || '');
        modalEl.classList.remove('hidden');
        if (typeof document !== 'undefined' && document.body) {
            state.priorBodyOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        }
        fetchAndRender();
    }

    function close() {
        if (!modalEl) return;
        modalEl.classList.add('hidden');
        state.requestSeq++;
        state.photoId = '';
        if (typeof document !== 'undefined' && document.body) {
            document.body.style.overflow = state.priorBodyOverflow || '';
            state.priorBodyOverflow = null;
        }
    }

    window.openPhotoWebDetectionModal = open;
})();
